import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Bell, Check, CheckCheck, ShoppingBag, CreditCard, Truck, DollarSign, MessageCircle } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Button } from '../components/ui/button';
import api from '../lib/api';

const NOTIF_ICONS = {
  new_sale: ShoppingBag,
  payment_confirmed: CreditCard,
  product_shipped: Truck,
  payment_released: DollarSign,
  new_message: MessageCircle,
};

const NOTIF_COLORS = {
  new_sale: 'text-green-600 bg-green-50',
  payment_confirmed: 'text-blue-600 bg-blue-50',
  product_shipped: 'text-orange-600 bg-orange-50',
  payment_released: 'text-emerald-600 bg-emerald-50',
  new_message: 'text-violet-600 bg-violet-50',
};

function timeAgo(dateStr) {
  const now = new Date();
  const d = new Date(dateStr);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'agora';
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const pollRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get('/notifications/unread-count');
      setUnreadCount(data.unread_count);
    } catch { /* silent */ }
  }, [user]);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get('/notifications?limit=20');
      setNotifications(data.notifications);
      setUnreadCount(data.notifications.filter(n => !n.read).length);
    } catch { /* silent */ }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    fetchUnreadCount();
    pollRef.current = setInterval(fetchUnreadCount, 15000);
    return () => clearInterval(pollRef.current);
  }, [user, fetchUnreadCount]);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  const handleClick = async (notif) => {
    if (!notif.read) {
      try {
        await api.put(`/notifications/${notif._id}/read`);
        setNotifications(prev => prev.map(n => n._id === notif._id ? { ...n, read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch { /* silent */ }
    }
    setOpen(false);
    if (notif.link) navigate(notif.link);
  };

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch { /* silent */ }
  };

  if (!user) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-2 hover:bg-muted rounded-full transition-colors" data-testid="notification-bell">
          <Bell className="h-5 w-5 text-muted-foreground" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-5 w-5 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold ring-2 ring-white" data-testid="notification-badge">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 sm:w-96 p-0 max-h-[70vh] overflow-hidden" data-testid="notification-dropdown">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="font-semibold text-sm" style={{ fontFamily: 'Outfit' }}>Notificações</h3>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 text-muted-foreground" onClick={markAllRead} data-testid="mark-all-read-btn">
              <CheckCheck className="h-3.5 w-3.5" /> Marcar todas como lidas
            </Button>
          )}
        </div>
        <div className="overflow-y-auto max-h-[55vh]">
          {notifications.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="no-notifications">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-30" />
              Nenhuma notificação
            </div>
          ) : (
            notifications.map(n => {
              const Icon = NOTIF_ICONS[n.type] || Bell;
              const colorClass = NOTIF_COLORS[n.type] || 'text-gray-600 bg-gray-50';
              return (
                <button
                  key={n._id}
                  onClick={() => handleClick(n)}
                  className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-muted/50 transition-colors border-b border-border/50 ${!n.read ? 'bg-primary/[0.03]' : ''}`}
                  data-testid={`notification-item-${n._id}`}
                >
                  <div className={`shrink-0 h-9 w-9 rounded-full flex items-center justify-center ${colorClass}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm truncate ${!n.read ? 'font-semibold' : 'font-medium text-muted-foreground'}`}>{n.title}</p>
                      {!n.read && <div className="shrink-0 h-2 w-2 rounded-full bg-primary" />}
                    </div>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">{n.message}</p>
                    <p className="text-[10px] text-muted-foreground/70 mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
